import { Link, useLocation } from 'react-router-dom'
import { UserMenu } from './user-menu'
import {
  LayoutDashboard,
  Users,
  Send,
  FileText,
  MessageSquare,
  Settings,
  LifeBuoy,
  Gift,
  ChevronsLeft,
  ChevronsRight,
  UsersRound,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/stores/app-store'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTeamMe } from '@/features/team/use-team'

type NavItem = {
  readonly label: string
  readonly href: string
  readonly icon: React.ComponentType<{ readonly className?: string }>
}

// Customer-facing navigation. The DCA staff super-admin dashboard
// lives at /super-admin and is intentionally NOT linked from the
// customer sidebar — staff reach it by direct URL.
const NAV_ITEMS: readonly NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Campaigns', href: '/campaigns', icon: Send },
  { label: 'Leads', href: '/leads', icon: Users },
  { label: 'Content', href: '/content', icon: FileText },
  { label: 'Inbox', href: '/inbox', icon: MessageSquare },
  { label: 'Settings', href: '/settings', icon: Settings },
  { label: 'Refer & Earn', href: '/affiliate', icon: Gift },
  { label: 'Support', href: '/support', icon: LifeBuoy },
] as const

/** Shown only to managers of a client team (Enterprise teams, /team). */
const TEAM_NAV_ITEM: NavItem = { label: 'Team', href: '/team', icon: UsersRound }

/** Customer nav plus the Team entry when the user manages a client team. */
export function useNavItems(): readonly NavItem[] {
  const { data: team } = useTeamMe()
  if (!team?.is_manager) return NAV_ITEMS
  const at = NAV_ITEMS.findIndex((i) => i.href === '/inbox') + 1
  return [...NAV_ITEMS.slice(0, at), TEAM_NAV_ITEM, ...NAV_ITEMS.slice(at)]
}

/**
 * Brand block — uses the DealCloser Autopilot logo image so the mark stays
 * visually identical across marketing site, dashboard, and login.
 * Expanded: horizontal lockup (mark + "DealCloser / AUTOPILOT" wordmark).
 * Collapsed: just the silver-D + blue-C mark, fits the 64px rail.
 */
function Wordmark({ collapsed }: { readonly collapsed: boolean }) {
  if (collapsed) {
    return (
      <img
        src="/dealcloser-mark.png"
        alt="DealCloser Autopilot"
        className="h-8 w-auto select-none"
        draggable={false}
      />
    )
  }
  return (
    <img
      src="/dealcloser-horizontal.png"
      alt="DealCloser Autopilot"
      className="h-9 w-auto select-none"
      draggable={false}
    />
  )
}

function NavLink({
  item,
  isActive,
  collapsed,
}: {
  readonly item: NavItem
  readonly isActive: boolean
  readonly collapsed: boolean
}) {
  // Active state: cobalt-tinted bg + left accent bar + white text + soft glow.
  // Hover: just a surface lift — no full color swap, quieter than active.
  const linkContent = (
    <Link
      to={item.href}
      className={cn(
        'relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200',
        'hover:bg-[rgba(255,255,255,0.04)] hover:text-[var(--color-silver-bright)]',
        isActive
          ? 'bg-[rgba(28,63,255,0.12)] text-[var(--color-glow-white)] shadow-[0_0_20px_rgba(28,63,255,0.12)]'
          : 'text-[var(--color-silver)]',
        collapsed && 'justify-center px-2',
      )}
    >
      {isActive && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-full bg-[var(--color-cobalt-bright)]"
        />
      )}
      <item.icon
        className={cn(
          'h-[18px] w-[18px] shrink-0 transition-colors',
          isActive && 'text-[var(--color-cobalt-bright)]',
        )}
      />
      {!collapsed && <span>{item.label}</span>}
    </Link>
  )

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    )
  }

  return linkContent
}

export function Sidebar() {
  const location = useLocation()
  const { sidebarOpen, toggleSidebar } = useAppStore()
  const navItems = useNavItems()

  const isCollapsed = !sidebarOpen

  return (
    <aside
      className={cn(
        'hidden md:flex flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300',
        isCollapsed ? 'w-[var(--sidebar-width-collapsed)]' : 'w-[var(--sidebar-width)]',
      )}
    >
      {/* Logo / Brand */}
      <div
        className={cn(
          'flex h-14 items-center border-b border-sidebar-border px-4',
          isCollapsed && 'justify-center px-2',
        )}
      >
        <Wordmark collapsed={isCollapsed} />
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-2 py-4">
        {!isCollapsed && (
          <p className="eyebrow mb-3 px-3 text-[var(--color-silver)]">Navigate</p>
        )}
        <nav className="flex flex-col gap-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.href}
              item={item}
              isActive={
                location.pathname === item.href ||
                location.pathname.startsWith(item.href + '/') ||
                (item.href === '/dashboard' && location.pathname === '/')
              }
              collapsed={isCollapsed}
            />
          ))}
        </nav>
      </ScrollArea>

      <Separator />

      {/* Bottom: collapse toggle + user avatar */}
      <div
        className={cn(
          'flex flex-col gap-3 p-3',
          isCollapsed && 'items-center',
        )}
      >
        <button
          type="button"
          onClick={toggleSidebar}
          className={cn(
            'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-[var(--color-silver)] transition-colors hover:bg-[rgba(255,255,255,0.04)] hover:text-[var(--color-silver-bright)]',
            isCollapsed && 'justify-center px-2',
          )}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronsRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronsLeft className="h-4 w-4" />
              <span>Collapse</span>
            </>
          )}
        </button>

        <UserMenu collapsed={isCollapsed} />
      </div>
    </aside>
  )
}
